import React, { useEffect, useState } from 'react';
import { apiGet, apiPut } from '../../../API/Api_Url';
import { X, MapPin, Calendar, DollarSign, Home, Users, Clock, FileCheck } from 'lucide-react';
import { toast } from 'react-toastify';

function UserAllPriceProvidedConstructionRequirement({ RequirementState, setRequirementState }) {
    const [Requirements, setRequirements] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    const GetUserAllRequirements = async () => {
        try {
            setIsUploading(true);
            const response = await apiGet('api/user/GetAllUserPriceProvidedConstructionRequirement');
            setRequirements(response.data);
            setIsUploading(false);
        } catch (error) {
            setIsUploading(false);
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        GetUserAllRequirements();
    }, []);


    const [selectedBids, setSelectedBids] = useState([]);

    const handleToggleContractor = (reqId, bid) => {
        setSelectedBids((prev) => {
            const isSame =
                prev.length > 0 &&
                prev[0].requirementId === reqId &&
                prev[0].contractorId === bid.Contractor;

            if (isSame) {
                return [];
            }
            return [
                {
                    requirementId: reqId,
                    contractorId: bid.Contractor,
                    price: bid.Price,
                    siteVisit: false,
                    ProjectManager: false,
                    Reportfrequency: "Weekly",
                },
            ];
        });
    };


    const handleChangeOption = (reqId, contractorId, field, value) => {
        setSelectedBids((prev) =>
            prev.map((b) =>
                b.requirementId === reqId && b.contractorId === contractorId
                    ? { ...b, [field]: value }
                    : b
            )
        );
    };

    const UserSelectContractors = async () => {
        if (selectedBids.length === 0) return toast.error("Please select at least one contractor");

        const payload = {
            contractors: selectedBids.map((b) => ({
                contractorId: b.contractorId,
                price: b.price,
                siteVisit: b.siteVisit,
                ProjectManager: b.ProjectManager,
                Reportfrequency: b.Reportfrequency,
            })),
        };

        try {
            setIsUploading(true);
            const response = await apiPut(
                `/api/user/UserSelectContractorForRequirement/${selectedBids[0]?.requirementId}`,
                payload
            );
            if (response?.data) {
                toast.success(
                    response?.data?.message ||
                    response?.message ||
                    "Contractors selected successfully!"
                );
                GetUserAllRequirements();
                setIsUploading(false);
            } else {
                toast.error(response?.message || "Failed to select contractors");
                setIsUploading(false);
            }
        } catch (error) {
            console.error("Error selecting contractors:", error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };


    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'Approved':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'Rejected':
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const ImageModal = () => {
        if (!selectedImage) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                <div className="relative max-w-4xl max-h-full">
                    <button
                        onClick={() => setSelectedImage(null)}
                        className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
                    >
                        <X size={32} />
                    </button>
                    <img
                        src={selectedImage}
                        alt="Construction site"
                        className="max-w-full max-h-screen object-contain rounded-lg"
                    />
                </div>
            </div>
        );
    };

    const ImageGallery = ({ images }) => {
        if (!images || images.length === 0) return null;

        return (
            <div className="mb-4">
                {images.length === 1 ? (
                    <img
                        src={images[0]}
                        alt="Project"
                        className="h-48 w-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedImage(images[0])}
                    />
                ) : (
                    <div className="grid grid-cols-2 gap-2">
                        {images?.slice(0, 4).map((image, index) => (
                            <div key={index} className="relative">
                                <img
                                    src={image}
                                    alt={`Project ${index + 1}`}
                                    className={`w-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity ${index === 0 ? 'h-32' : 'h-20'
                                        }`}
                                    onClick={() => setSelectedImage(image)}
                                />
                                {index === 3 && images?.length > 4 && (
                                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center text-white font-semibold">
                                        +{images?.length - 4} more
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
            <div className="max-w-7xl mx-auto">
                <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-1 md:mb-0">
                            My Price Provided Construction Requirements
                        </h1>
                        <p className="text-gray-600">
                            Manage and track your construction projects
                        </p>
                    </div>

                    <div className="mt-4 md:mt-0">
                        <select
                            value={RequirementState}
                            onChange={(e) => setRequirementState(e.target.value)}
                            className="border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0060B9]"
                        >
                            <option value="All">All</option>
                            <option value="pending">Pending</option>
                            <option value="in_review">In Review</option>
                            <option value="price_provided">Price Provided</option>
                            <option value="approved">Approved</option>
                            <option value="AgreementPending">Agreement Pending</option>
                            <option value="InConstruction">In-Construction</option>
                            <option value="Completed">Completed</option>
                        </select>
                    </div>
                </div>
                {isUploading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                        <p className="ml-4 text-gray-600">Loading your projects...</p>
                    </div>
                ) : Requirements?.length === 0 ? (
                    <div className="text-center py-20">
                        <Home className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                        <p className="text-xl text-gray-500">No requirements found.</p>
                        <p className="text-gray-400 mt-2">Create your first construction project to get started.</p>
                    </div>
                ) : (
                    <div className="grid gap-6 grid-cols-1">
                        {Requirements?.map((req) => (
                            <div
                                key={req?._id}
                                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                            >
                                <ImageGallery images={req?.SitePhotosOrPlan} />

                                <div className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <h3 className="text-xl font-bold text-gray-900">{req?.ProjectType}</h3>
                                        <span
                                            className={`px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(req?.Status)}`}
                                        >
                                            {req?.Status}
                                        </span>
                                    </div>

                                    <div className="flex items-start text-gray-600 mb-4">
                                        <MapPin className="flex-shrink-0 h-4 w-4 mr-2 mt-0.5" />
                                        <p className="text-sm">
                                            <a
                                                href={`https://www.google.com/maps?q=${req?.Location?.Late},${req?.Location?.Long}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                {req?.Location?.Address}, {req?.City}, {req?.State} - {req?.Pincode}
                                            </a>
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div className="flex items-center text-gray-700">
                                            <Home className="h-4 w-4 mr-2 text-blue-500" />
                                            <div>
                                                <p className="font-medium">Size</p>
                                                <p className="text-gray-600">
                                                    {req?.SizeDimension?.Length} x {req?.SizeDimension?.Breadth}{" "} x {req?.NumberOfFloors} ({req?.SizeDimension?.Area}{" "})
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center text-gray-700">
                                            <Users className="h-4 w-4 mr-2 text-green-500" />
                                            <div>
                                                <p className="font-medium">Floors</p>
                                                <p className="text-gray-600">{req?.NumberOfFloors}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center text-gray-700">
                                            <DollarSign className="h-4 w-4 mr-2 text-purple-500" />
                                            <div>
                                                <p className="font-medium">Budget</p>
                                                <p className="text-gray-600">{req?.Budget}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center text-gray-700">
                                            <Clock className="h-4 w-4 mr-2 text-orange-500" />
                                            <div>
                                                <p className="font-medium">Timeline</p>
                                                <p className="text-gray-600">{req?.TimelineInMonths} months</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 pt-4 border-t border-gray-100">
                                        <p className="text-sm text-gray-600 mb-2">
                                            <span className="font-medium">Payment:</span> {req?.PaymentMethod}
                                        </p>
                                    </div>
                                    {req?.AdminSelectedbids?.length > 0 && (
                                        <div className="mt-6 border-t border-gray-200 pt-5">
                                            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                                <Users className="h-5 w-5 mr-2 text-blue-500" />
                                                Contractor Bids
                                            </h4>

                                            <div className="grid gap-4">
                                                {req?.AdminSelectedbids?.map((bid) => {
                                                    const isSelected =
                                                        selectedBids.length > 0 &&
                                                        selectedBids[0].requirementId === req?._id &&
                                                        selectedBids[0].contractorId === bid?.Contractor;

                                                    return (
                                                        <div
                                                            key={bid?._id}
                                                            className={`p-4 border rounded-xl shadow-sm transition-all duration-200 ${isSelected
                                                                ? "border-blue-500 bg-blue-50 ring-2 ring-blue-300"
                                                                : "border-gray-200 hover:shadow-md"
                                                                }`}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div>
                                                                    <h5 className="text-base font-semibold text-gray-800">
                                                                        {bid?.Name}
                                                                    </h5>
                                                                    <p className="text-sm text-gray-500">
                                                                        Price: <span className="font-medium">{bid?.Price}</span>
                                                                    </p>
                                                                </div>

                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={() => handleToggleContractor(req?._id, bid)}
                                                                    className="h-5 w-5 text-blue-600 rounded"
                                                                />
                                                            </div>

                                                            {isSelected && (
                                                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                                    <select
                                                                        value={
                                                                            selectedBids.find(
                                                                                (b) => b.requirementId === req?._id && b.contractorId === bid?.Contractor
                                                                            )?.siteVisit
                                                                                ? "true"
                                                                                : "false"
                                                                        }
                                                                        onChange={(e) =>
                                                                            handleChangeOption(
                                                                                req?._id,
                                                                                bid?.Contractor,
                                                                                "siteVisit",
                                                                                e.target.value === "true"
                                                                            )
                                                                        }
                                                                        className="w-full border rounded-lg p-2 text-sm"
                                                                    >
                                                                        <option value="false">Site Visit: No</option>
                                                                        <option value="true">Site Visit: Yes</option>
                                                                    </select>

                                                                    <select
                                                                        value={
                                                                            selectedBids.find(
                                                                                (b) => b.requirementId === req?._id && b.contractorId === bid?.Contractor
                                                                            )?.ProjectManager
                                                                                ? "true"
                                                                                : "false"
                                                                        }
                                                                        onChange={(e) =>
                                                                            handleChangeOption(
                                                                                req?._id,
                                                                                bid?.Contractor,
                                                                                "ProjectManager",
                                                                                e.target.value === "true"
                                                                            )
                                                                        }
                                                                        className="w-full border rounded-lg p-2 text-sm"
                                                                    >
                                                                        <option value="false">Project Manager: No</option>
                                                                        <option value="true">Project Manager: Yes</option>
                                                                    </select>
                                                                    <select
                                                                        value={
                                                                            selectedBids.find(
                                                                                (b) => b.requirementId === req?._id && b.contractorId === bid?.Contractor
                                                                            )?.Reportfrequency || "Weekly"
                                                                        }
                                                                        onChange={(e) =>
                                                                            handleChangeOption(
                                                                                req?._id,
                                                                                bid?.Contractor,
                                                                                "Reportfrequency",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        className="w-full border rounded-lg p-2 text-sm"
                                                                    >
                                                                        <option value="Daily">Report: Daily</option>
                                                                        <option value="Weekly">Report: Weekly</option>
                                                                        <option value="Monthly">Report: Monthly</option>
                                                                    </select>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <button
                                                disabled={selectedBids.length === 0}
                                                className={`mt-5 px-5 py-2.5 rounded-lg text-sm font-medium shadow ${selectedBids.length > 0
                                                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                                                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                    }`}
                                                onClick={() => UserSelectContractors()}
                                            >
                                                Confirm Selection
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center">
                                    <div className="flex items-center text-sm text-gray-500">
                                        <Calendar className="h-4 w-4 mr-1" />
                                        <span>Created: {formatDate(req?.createdAt)}</span>
                                    </div>
                                    {req?.NeedLocalAuthorityApproval && (
                                        <div className="flex items-center text-sm text-red-600 font-medium">
                                            <FileCheck className="h-4 w-4 mr-1" />
                                            <span>Authority Approval</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <ImageModal />
        </div>
    );
}

export default UserAllPriceProvidedConstructionRequirement;

